// Professional Animation System for WhatsApp Portal
// Designed for smooth, purposeful, and accessible animations

// Animation duration system (in milliseconds)
export const duration = {
  // Micro animations - instant feedback
  instant: 100,        // Icon changes, color transitions
  fast: 150,          // Button hover, focus states
  quick: 200,         // Small element movements, tooltips
  
  // Standard animations - common interactions
  standard: 300,      // Modal open/close, tab switches
  moderate: 400,      // Card hover, drawer slide
  comfortable: 500,   // Page transitions, large movements
  
  // Complex animations - significant state changes
  deliberate: 700,    // Complex transitions, multi-step animations
  slow: 1000,        // Hero animations, attention-grabbing effects
  
  // Loading and progress animations
  loading: 1200,     // Skeleton loading, progress indicators
  heartbeat: 2000,   // Pulse animations, breathing effects
};

// Professional easing curves for business applications
export const easing = {
  // Standard Material Design curves
  linear: 'cubic-bezier(0, 0, 1, 1)',
  
  // Entrance animations (ease-out family)
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',          // Standard ease-out
  easeOutQuart: 'cubic-bezier(0.25, 1, 0.5, 1)',  // Smooth deceleration
  easeOutExpo: 'cubic-bezier(0.16, 1, 0.3, 1)',   // Dramatic slow-down
  
  // Exit animations (ease-in family)
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',           // Standard ease-in
  easeInQuart: 'cubic-bezier(0.5, 0, 0.75, 0)',   // Smooth acceleration
  easeInExpo: 'cubic-bezier(0.7, 0, 0.84, 0)',    // Dramatic speed-up
  
  // Symmetrical animations (ease-in-out family)
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',      // Standard ease-in-out
  easeInOutQuart: 'cubic-bezier(0.76, 0, 0.24, 1)', // Smooth both ends
  easeInOutExpo: 'cubic-bezier(0.87, 0, 0.13, 1)',  // Dramatic both ends
  
  // Special purpose curves
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Playful bounce
  anticipate: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Slight pull-back
  overshoot: 'cubic-bezier(0.25, 0.46, 0.45, 1.1)',   // Slight overshoot
  
  // Professional business curves (subtle, purposeful)
  professional: 'cubic-bezier(0.25, 0.1, 0.25, 1)',   // Smooth, confident
  gentle: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',     // Soft, approachable
  crisp: 'cubic-bezier(0.4, 0, 0.6, 1)',              // Clean, precise
};

// Animation presets for common use cases
export const animations = {
  // Fade animations
  fadeIn: {
    keyframes: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    duration: duration.standard,
    easing: easing.easeOut,
    fillMode: 'both',
  },
  
  fadeOut: {
    keyframes: {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
    duration: duration.quick,
    easing: easing.easeIn,
    fillMode: 'both',
  },
  
  // Slide animations
  slideInUp: {
    keyframes: {
      from: { 
        opacity: 0,
        transform: 'translateY(32px)',
      },
      to: { 
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    duration: duration.standard,
    easing: easing.easeOutQuart,
    fillMode: 'both',
  },
  
  slideInDown: {
    keyframes: {
      from: { 
        opacity: 0,
        transform: 'translateY(-32px)',
      },
      to: { 
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    duration: duration.standard,
    easing: easing.easeOutQuart,
    fillMode: 'both',
  },
  
  slideInLeft: {
    keyframes: {
      from: { 
        opacity: 0,
        transform: 'translateX(-32px)',
      },
      to: { 
        opacity: 1,
        transform: 'translateX(0)',
      },
    },
    duration: duration.standard,
    easing: easing.easeOutQuart,
    fillMode: 'both',
  },
  
  slideInRight: {
    keyframes: {
      from: { 
        opacity: 0,
        transform: 'translateX(32px)',
      },
      to: { 
        opacity: 1,
        transform: 'translateX(0)',
      },
    },
    duration: duration.standard,
    easing: easing.easeOutQuart,
    fillMode: 'both',
  },
  
  // Scale animations
  scaleIn: {
    keyframes: {
      from: { 
        opacity: 0,
        transform: 'scale(0.9)',
      },
      to: { 
        opacity: 1,
        transform: 'scale(1)',
      },
    },
    duration: duration.standard,
    easing: easing.easeOutQuart,
    fillMode: 'both',
  },
  
  scaleOut: {
    keyframes: {
      from: { 
        opacity: 1,
        transform: 'scale(1)',
      },
      to: { 
        opacity: 0,
        transform: 'scale(0.9)',
      },
    },
    duration: duration.quick,
    easing: easing.easeInQuart,
    fillMode: 'both',
  },
  
  // Bounce animation for attention
  bounce: {
    keyframes: {
      '0%, 20%, 53%, 80%, 100%': {
        transform: 'translateY(0)',
      },
      '40%, 43%': {
        transform: 'translateY(-8px)',
      },
      '70%': {
        transform: 'translateY(-4px)',
      },
      '90%': {
        transform: 'translateY(-2px)',
      },
    },
    duration: duration.slow,
    easing: easing.easeInOut,
    iterationCount: 'infinite',
  },
  
  // Pulse animation for loading states
  pulse: {
    keyframes: {
      '0%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '50%': {
        opacity: 0.7,
        transform: 'scale(0.98)',
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1)',
      },
    },
    duration: duration.heartbeat,
    easing: easing.easeInOut,
    iterationCount: 'infinite',
  },
  
  // Shimmer effect for loading skeletons
  shimmer: {
    keyframes: {
      '0%': {
        backgroundPosition: '-200px 0',
      },
      '100%': {
        backgroundPosition: 'calc(200px + 100%) 0',
      },
    },
    duration: duration.loading,
    easing: easing.linear,
    iterationCount: 'infinite',
  },
  
  // Rotate animation for loading spinners
  rotate: {
    keyframes: {
      from: {
        transform: 'rotate(0deg)',
      },
      to: {
        transform: 'rotate(360deg)',
      },
    },
    duration: duration.slow,
    easing: easing.linear,
    iterationCount: 'infinite',
  },
};

// Micro-interaction animations for specific components
export const microInteractions = {
  // Button interactions
  buttonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: `all ${duration.fast}ms ${easing.easeOut}`,
  },
  
  buttonPress: {
    transform: 'translateY(0)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    transition: `all ${duration.instant}ms ${easing.easeIn}`,
  },
  
  // Card interactions
  cardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    transition: `all ${duration.standard}ms ${easing.easeOutQuart}`,
  },
  
  cardPress: {
    transform: 'translateY(-2px)',
    transition: `all ${duration.instant}ms ${easing.easeIn}`,
  },
  
  // Input field interactions
  inputFocus: {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(37, 211, 102, 0.16)',
    transition: `all ${duration.quick}ms ${easing.easeOut}`,
  },
  
  inputBlur: {
    transform: 'translateY(0)',
    boxShadow: 'none',
    transition: `all ${duration.quick}ms ${easing.easeIn}`,
  },
  
  // Icon interactions
  iconHover: {
    transform: 'scale(1.1)',
    transition: `all ${duration.fast}ms ${easing.easeOut}`,
  },
  
  iconPress: {
    transform: 'scale(0.95)',
    transition: `all ${duration.instant}ms ${easing.easeIn}`,
  },
  
  // List item interactions
  listItemHover: {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    paddingLeft: '20px',
    transition: `all ${duration.quick}ms ${easing.easeOut}`,
  },
};

// Page transition animations
export const pageTransitions = {
  // Slide transitions for navigation
  slideLeft: {
    enter: {
      transform: 'translateX(100%)',
      opacity: 0,
    },
    enterActive: {
      transform: 'translateX(0)',
      opacity: 1,
      transition: `all ${duration.comfortable}ms ${easing.easeOutQuart}`,
    },
    exit: {
      transform: 'translateX(0)',
      opacity: 1,
    },
    exitActive: {
      transform: 'translateX(-100%)',
      opacity: 0,
      transition: `all ${duration.comfortable}ms ${easing.easeInQuart}`,
    },
  },
  
  // Fade transition for modals
  fade: {
    enter: {
      opacity: 0,
    },
    enterActive: {
      opacity: 1,
      transition: `opacity ${duration.standard}ms ${easing.easeOut}`,
    },
    exit: {
      opacity: 1,
    },
    exitActive: {
      opacity: 0,
      transition: `opacity ${duration.quick}ms ${easing.easeIn}`,
    },
  },
  
  // Scale transition for dialogs
  scale: {
    enter: {
      transform: 'scale(0.9)',
      opacity: 0,
    },
    enterActive: {
      transform: 'scale(1)',
      opacity: 1,
      transition: `all ${duration.standard}ms ${easing.easeOutQuart}`,
    },
    exit: {
      transform: 'scale(1)',
      opacity: 1,
    },
    exitActive: {
      transform: 'scale(0.9)',
      opacity: 0,
      transition: `all ${duration.quick}ms ${easing.easeInQuart}`,
    },
  },
};

// Loading state animations
export const loadingStates = {
  // Skeleton shimmer effect
  skeleton: {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: `shimmer ${duration.loading}ms ${easing.linear} infinite`,
  },
  
  // Spinner animation
  spinner: {
    animation: `rotate ${duration.slow}ms ${easing.linear} infinite`,
  },
  
  // Dots animation
  dots: {
    animation: `pulse ${duration.heartbeat}ms ${easing.easeInOut} infinite`,
  },
  
  // Progress bar animation
  progressBar: {
    transition: `width ${duration.standard}ms ${easing.easeOut}`,
  },
};

// Animation utility functions
export const animationUtils = {
  // Create CSS animation string
  createAnimation: (name, duration, easing, delay = 0, fillMode = 'both') => {
    return `${name} ${duration}ms ${easing} ${delay}ms ${fillMode}`;
  },
  
  // Create transition string
  createTransition: (properties, duration, easing, delay = 0) => {
    if (Array.isArray(properties)) {
      return properties
        .map(prop => `${prop} ${duration}ms ${easing} ${delay}ms`)
        .join(', ');
    }
    return `${properties} ${duration}ms ${easing} ${delay}ms`;
  },
  
  // Stagger animation delays for lists
  staggerDelay: (index, baseDelay = 50) => index * baseDelay,
  
  // Responsive animation duration (reduced for mobile)
  getResponsiveDuration: (baseDuration, isMobile = false) => {
    return isMobile ? Math.max(baseDuration * 0.7, 150) : baseDuration;
  },
  
  // Check if user prefers reduced motion
  respectsReducedMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
};

// Animation guidelines and best practices
export const animationGuidelines = {
  principles: {
    title: 'Animation Principles',
    rules: [
      'Animations should feel natural and purposeful, never arbitrary',
      'Use consistent timing and easing across similar interactions',
      'Respect user preferences for reduced motion',
      'Ensure animations enhance rather than distract from content',
      'Keep animations subtle and professional for business applications',
    ],
  },
  
  performance: {
    title: 'Performance Guidelines',
    best_practices: [
      'Use CSS transforms and opacity for smooth animations',
      'Avoid animating layout properties (width, height, margin, padding)',
      'Use will-change property sparingly and remove after animation',
      'Limit concurrent animations to maintain 60fps',
      'Use requestAnimationFrame for JavaScript animations',
    ],
  },
  
  accessibility: {
    title: 'Accessibility Considerations',
    requirements: [
      'Respect prefers-reduced-motion user setting',
      'Provide alternative feedback for users who disable animations',
      'Ensure animations don\'t trigger seizures or vestibular disorders',
      'Use semantic markup and ARIA attributes for dynamic content',
      'Test with screen readers and keyboard navigation',
    ],
  },
  
  timing: {
    title: 'Animation Timing Guidelines',
    recommendations: [
      'Micro-interactions: 100-200ms for instant feedback',
      'Standard UI: 200-500ms for common interactions',
      'Complex transitions: 500-800ms for significant changes',
      'Loading states: 1000ms+ for ongoing processes',
      'Attention-seeking: Use sparingly and with purpose',
    ],
  },
};

// CSS keyframes for animations (to be injected into global styles)
export const keyframes = {
  fadeIn: `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `,
  
  slideInUp: `
    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(32px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
  
  scaleIn: `
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
  
  shimmer: `
    @keyframes shimmer {
      0% { background-position: -200px 0; }
      100% { background-position: calc(200px + 100%) 0; }
    }
  `,
  
  rotate: `
    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `,
  
  pulse: `
    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.7;
        transform: scale(0.98);
      }
    }
  `,
};

export default {
  duration,
  easing,
  animations,
  microInteractions,
  pageTransitions,
  loadingStates,
  animationUtils,
  animationGuidelines,
  keyframes,
};